// User journey tracking utility

interface PageVisit {
  path: string
  timestamp: number
  title: string
}

interface UserJourney {
  referrer: string
  landingPage: string
  pagesVisited: PageVisit[]
  conversionButton?: string
  sessionStart: number
  sessionDuration?: number
}

const STORAGE_KEY = 'user_journey'

export function initializeTracking() {
  if (typeof window === 'undefined') return

  const existingJourney = getJourney()
  
  if (!existingJourney) {
    const journey: UserJourney = {
      referrer: document.referrer || 'Direct',
      landingPage: window.location.pathname,
      pagesVisited: [{
        path: window.location.pathname,
        timestamp: Date.now(),
        title: document.title
      }],
      sessionStart: Date.now()
    }
    
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(journey))
  }
}

export function trackPageVisit() {
  if (typeof window === 'undefined') return

  const journey = getJourney()
  if (!journey) {
    initializeTracking()
    return
  }

  const currentPath = window.location.pathname
  const lastVisit = journey.pagesVisited[journey.pagesVisited.length - 1]
  
  // Only add if it's a different page
  if (!lastVisit || lastVisit.path !== currentPath) {
    journey.pagesVisited.push({
      path: currentPath,
      timestamp: Date.now(),
      title: document.title
    })
    
    // Keep only last 20 pages to avoid storage issues
    if (journey.pagesVisited.length > 20) {
      journey.pagesVisited = journey.pagesVisited.slice(-20)
    }
    
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(journey))
  }
}

export function trackConversion(buttonName: string) {
  if (typeof window === 'undefined') return

  const journey = getJourney()
  if (journey) {
    journey.conversionButton = buttonName
    journey.sessionDuration = Date.now() - journey.sessionStart
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(journey))
  }
}

export function getJourney(): UserJourney | null {
  if (typeof window === 'undefined') return null
  
  const stored = sessionStorage.getItem(STORAGE_KEY)
  return stored ? JSON.parse(stored) : null
}

export function getJourneyReport(): string {
  const journey = getJourney()
  if (!journey) return 'No tracking data available'

  const duration = journey.sessionDuration || (Date.now() - journey.sessionStart)
  const durationMinutes = Math.round(duration / 60000)
  
  const report = `
=== USER JOURNEY TRACKING ===

Referrer: ${journey.referrer}
Landing Page: ${journey.landingPage}
Conversion Button: ${journey.conversionButton || 'Unknown'}
Session Duration: ${durationMinutes} minutes

Pages Visited (${journey.pagesVisited.length}):
${journey.pagesVisited.map((visit, index) => {
  const timeSpent = index < journey.pagesVisited.length - 1 
    ? Math.round((journey.pagesVisited[index + 1].timestamp - visit.timestamp) / 1000)
    : Math.round((Date.now() - visit.timestamp) / 1000)
  return `${index + 1}. ${visit.path} (${timeSpent}s)`
}).join('\n')}

===========================
`
  
  return report
}

// Track UTM parameters
export function getUTMParameters() {
  if (typeof window === 'undefined') return {}
  
  const params = new URLSearchParams(window.location.search)
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_term: params.get('utm_term') || '',
    utm_content: params.get('utm_content') || ''
  }
}

export function getTrackingData() {
  const journey = getJourney()
  const utm = getUTMParameters()

  // Get browser info
  const getBrowser = () => {
    if (typeof navigator === 'undefined') return 'Unknown'
    const ua = navigator.userAgent
    if (ua.includes('Chrome')) return 'Chrome'
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari'
    if (ua.includes('Firefox')) return 'Firefox'
    if (ua.includes('Edge')) return 'Edge'
    return 'Other'
  }

  // Get device type
  const getDevice = () => {
    if (typeof navigator === 'undefined') return 'Unknown'
    const ua = navigator.userAgent
    if (/Mobile|Android|iPhone|iPad/i.test(ua)) {
      if (/iPad/i.test(ua)) return 'iPad'
      if (/iPhone/i.test(ua)) return 'iPhone'
      if (/Android/i.test(ua)) return 'Android'
      return 'Mobile'
    }
    return 'Desktop'
  }

  // Get OS
  const getOS = () => {
    if (typeof navigator === 'undefined') return 'Unknown'
    const ua = navigator.userAgent
    if (/iPhone|iPad/i.test(ua)) return 'iOS' // Check iOS first since it includes "Mac" in UA
    if (ua.includes('Android')) return 'Android'
    if (ua.includes('Windows')) return 'Windows'
    if (ua.includes('Mac')) return 'MacOS'
    if (ua.includes('Linux')) return 'Linux'
    return 'Unknown'
  }

  return {
    ...journey,
    ...utm,
    currentUrl: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    screenResolution: typeof window !== 'undefined' ? `${window.screen.width}x${window.screen.height}` : '',
    browser: getBrowser(),
    device: getDevice(),
    os: getOS(),
    language: typeof navigator !== 'undefined' ? navigator.language : 'Unknown',
    timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'Unknown',
    landingPage: journey?.landingPage || '/',
    referrer: journey?.referrer || 'Direct',
    utmSource: utm.utm_source,
    utmMedium: utm.utm_medium,
    utmCampaign: utm.utm_campaign,
    utmTerm: utm.utm_term,
    utmContent: utm.utm_content
  }
}